import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Reservation } from '../models/reservation';
import { ReservationService } from './reservation-http-service';
import { RoomTypeRateSummary, RoomTypeRateSummarySort } from '../models/room-type-rate-summary';
import { StaySummary } from '../models/stay-summary';
import { ReservationStatusCode } from 'app/event/models/reservation-status-code.enum';
import { RoomStaySummary } from '../models/room-stay-summary';
import { ReservationUtils } from '../utils/reservation-utils';
import { EventStore } from 'app/event/service/event.store';
import { OnyxEvent } from '../models/event';

@Injectable()
export class ReservationStore {

  private currentReservations: BehaviorSubject<List<Reservation>> = new BehaviorSubject(List([]));
  private currentRoomSummary: BehaviorSubject<List<RoomTypeRateSummary>> = new BehaviorSubject(List([]));
  private currentStaySummary: BehaviorSubject<StaySummary> = new BehaviorSubject(null);

  constructor(
    private flashMessagesService: FlashMessagesService,
    private reservationHttpService: ReservationService,
    private eventStore: EventStore
  ) {}

  get reservations(): Observable<List<Reservation>> {
    return this.currentReservations.asObservable();
  }

  get roomSummary(): Observable<List<RoomTypeRateSummary>> {
    return this.currentRoomSummary.asObservable();
  }

  get staySummary(): Observable<StaySummary> {
    return this.currentStaySummary.asObservable();
  }

  /**
   * Has to be called to retrieve reservations.
   * Is called by resolver.
   *
   * @param eventId
   * @returns RESERVATION_LIST
   */
  getReservations(eventId: number): Observable<List<Reservation>> {

    return this.reservationHttpService.get(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.COMMISSION.ROOM_DETAIL',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<Reservation>) => {
        this.currentReservations.next(reservationList);

        let summary: RoomStaySummary = this.transformReservationsToSummary(reservationList);

        this.currentRoomSummary.next(summary.roomTypeRateSummaryList);
        this.currentStaySummary.next(summary.staySummary);
        return reservationList;
      });
  }

  /**
   * Filters reservation list by a status code
   * e.g. checked out
   *
   * @param statusCode
   * @returns {Observable<List<Reservation>>}
   */
  getReservationListFilteredByStatus(statusCode: ReservationStatusCode): Observable<List<Reservation>> {
    return this.currentReservations.asObservable().map((reservationList: List<Reservation>) => {
      return reservationList.filter((reservation: Reservation) => {
        return reservation.status === statusCode.toString();
      }).toList();
    });
  }

  /**
   * Gets the filtered reservation list by status code and transforms the list into a room stay summary
   *
   * @param statusCode{ReservationStatusCode}
   * @returns {Observable<StaySummary>}
   */
  getRoomStaySummaryFilteredByStatus(statusCode: ReservationStatusCode): Observable<RoomStaySummary> {
    return this.getReservationListFilteredByStatus(statusCode).map(reservationList => {
      return this.transformReservationsToSummary(reservationList);
    });
  }

  getTotalCommissions(reservationStatusCode: ReservationStatusCode): Observable<number> {
    let commissionPercentage = null;
    return this.eventStore.selectedEvent.flatMap((event: OnyxEvent) => {
      commissionPercentage = event.commissionRoomPercentage;
      return this.getRoomStaySummaryFilteredByStatus(reservationStatusCode);
    }).map((summary: RoomStaySummary) => {
      const staySummary = summary.staySummary;
      const totalRevenue = staySummary.totalRevenue;
      const totalCommission = ReservationUtils.calculateCommission(
        totalRevenue,
        commissionPercentage
      );
      return totalCommission;
    });
  }

  /**
   * Transforms a list of Reservations into a RoomStaySummary object
   * Contains a stay summary and a room summary
   *
   * Immutable JS doc on groupBy
   * https://facebook.github.io/immutable-js/docs/#/List/groupBy
   *
   * @param reservations {List<Reservation>}
   * @returns RoomStaySummary
   */
  private transformReservationsToSummary(reservations: List<Reservation>): RoomStaySummary {
    let staySummary = new StaySummary();
    let roomTypeRateSummaryList = List<RoomTypeRateSummary>([]);

    // create lists of Reservations grouped by type and rate
    let listOfReservationsGroupedByTypeAndRate = List<List<Reservation>>([]);

    // this has to be re-created as a list or else groupBy is not available
    List(reservations).groupBy(roomTypeGroup => roomTypeGroup.roomType)
      .forEach(roomTypeGroup => {
        roomTypeGroup
          .groupBy(roomRateGroup => roomRateGroup.roomRate)
          .forEach(roomRateGroup => {
            listOfReservationsGroupedByTypeAndRate = listOfReservationsGroupedByTypeAndRate.push(roomRateGroup.toList());
          });
      });

    // iterate through each list (grouped by type and rate)
    // and generate a room summary
    listOfReservationsGroupedByTypeAndRate.forEach((roomTypeAndRateGroup: List<Reservation>) => {
      const roomTypeRateSummary: RoomTypeRateSummary = this.transformReservationListToRoomSummary(roomTypeAndRateGroup);

      // updating stay summary totals each iteration
      staySummary = new StaySummary({
        totalNights: staySummary.totalNights + roomTypeRateSummary.totalNights,
        totalRevenue: staySummary.totalRevenue + roomTypeRateSummary.totalRevenue
      });

      // adding room type and rate summary to list
      roomTypeRateSummaryList = roomTypeRateSummaryList.push(roomTypeRateSummary);
    });

    // build the room stay Summary
    return new RoomStaySummary({
      roomTypeRateSummaryList: roomTypeRateSummaryList.sort(RoomTypeRateSummarySort).toList(),
      staySummary: staySummary
    });
  }

  /**
   * Transforms a list of reservations into a RoomTypeRateSummary
   *
   * @param roomList {List<Reservation>}
   * @returns RoomTypeRateSummary
   */
  private transformReservationListToRoomSummary(roomList: List<Reservation>): RoomTypeRateSummary {
    let roomTypeRateSummary = new RoomTypeRateSummary();

    roomList.forEach((room: Reservation) => {
      roomTypeRateSummary = new RoomTypeRateSummary({
        roomType: room.roomType,
        roomRate: room.roomRate,
        totalNights: roomTypeRateSummary.totalNights + room.nights,
        totalRevenue: roomTypeRateSummary.totalRevenue + (room.roomRate * room.nights)
      });
    });

    return roomTypeRateSummary;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/service/reservation-store.ts